import axios from "axios";
import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import "../css/ActionMenu.css";
import { fetchToken, getUserRole, getUserSecretariaId } from "./Auth";

// --- Componente de Menu (Dropdown) ---
const ActionMenu = ({ projeto, canEdit }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="action-menu-container" ref={menuRef}>
      <button className="btn-action-trigger" onClick={() => setIsOpen(!isOpen)}>
        Opções
      </button>
      {isOpen && (
        <div className="action-dropdown">
          <Link
            to={`/viewprojetocompleto/${projeto.id}`}
            className="dropdown-item"
          >
            👁️ Visualizar
          </Link>
          <Link to={`/gantt3/${projeto.id}`} className="dropdown-item">
            📊 Gantt
          </Link>

          {/* O botão Editar só aparece se canEdit for true */}
          {canEdit && (
            <Link
              to={`/editprojetocompleto/${projeto.id}`}
              className="dropdown-item item-edit"
            >
              ✏️ Editar
            </Link>
          )}
        </div>
      )}
    </div>
  );
};

// --- Componente Principal ---
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

function ProjetoCompleto() {
  const [projetos, setProjetos] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationInfo, setPaginationInfo] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  // Dados do Usuário
  const userRole = getUserRole();
  const userSecretariaId = getUserSecretariaId();

  useEffect(() => {
    const token = fetchToken();

    if (!token) {
      navigate("/login");
      return;
    }

    const params = new URLSearchParams(location.search);
    const idSecretaria = params.get("id_secretaria");
    const status = params.get("status");

    let url = `/api/projetocompleto?page=${currentPage}&limit=10`;

    if (idSecretaria) url += `&id_secretaria=${idSecretaria}`;
    if (debouncedSearchTerm) url += `&search=${debouncedSearchTerm}`;
    if (status) url += `&status=${status}`;

    axios
      .get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((res) => {
        if (res.data.Status === "Success") {
          setProjetos(res.data.Result);
          setPaginationInfo(res.data.Pagination);
        } else {
          alert("Erro ao buscar dados");
        }
      })
      .catch((err) => {
        console.error("Erro na requisição:", err);
        if (
          err.response &&
          (err.response.status === 401 || err.response.status === 403)
        ) {
          navigate("/login");
        }
      });
  }, [location.search, currentPage, debouncedSearchTerm, navigate]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const goToNextPage = () => {
    if (paginationInfo && currentPage < paginationInfo.totalPages) {
      setCurrentPage((page) => page + 1);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage((page) => page - 1);
    }
  };

  // --- CORREÇÃO AQUI ---
  // Simplificamos a lógica: se o projeto veio da API (que já filtra por permissão),
  // o usuário tem direito de interagir com ele.
  // O backend bloqueará o PUT/DELETE se houver tentativa de bypass.
  const podeEditar = (projetoSecretariaId) => {
    return true;
  };

  return (
    <div className="px-5 py-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3
          className="m-0"
          style={{ borderBottom: "3px solid #3498db", paddingBottom: "10px" }}
        >
          Lista de Processos
        </h3>
        {(userRole === "admin" || userRole === "user") && (
          <Link to="/createprojetocompleto" className="btn btn-primary">
            Adicionar Projeto
          </Link>
        )}
      </div>

      <div className="mb-3">
        <input
          type="text"
          className="form-control"
          placeholder="Pesquisar..."
          value={searchTerm}
          onChange={handleSearch}
        />
      </div>

      <div className="table-responsive" style={{ minHeight: "400px" }}>
        <table className="table table-striped align-middle">
          <thead>
            <tr>
              <th>Secretaria</th>
              <th>Nome do Processo</th>
              <th>Gestor</th>
              <th className="text-center" style={{ width: "120px" }}>
                Ações
              </th>
            </tr>
          </thead>
          <tbody>
            {projetos.length === 0 ? (
              <tr>
                <td colSpan={4} className="text-center text-muted">
                  Nenhum projeto encontrado ou acesso não autorizado.
                </td>
              </tr>
            ) : (
              projetos.map((completo) => (
                <tr key={completo.id}>
                  <td>{completo.secretaria_nome}</td>
                  <td>{completo.nomeprojeto}</td>
                  <td>{completo.gestor}</td>
                  <td className="text-center">
                    {/* Passamos true para canEdit, pois confiamos no filtro do Backend */}
                    <ActionMenu
                      projeto={completo}
                      canEdit={podeEditar(completo.id_secretaria)}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {paginationInfo && paginationInfo.totalPages > 1 && (
        <nav className="d-flex justify-content-between align-items-center mt-3">
          <span className="text-muted small">
            Página {paginationInfo.page} de {paginationInfo.totalPages}
          </span>
          <div>
            <button
              className="btn btn-outline-primary me-2"
              onClick={goToPreviousPage}
              disabled={currentPage === 1}
            >
              Anterior
            </button>
            <button
              className="btn btn-outline-primary"
              onClick={goToNextPage}
              disabled={currentPage === paginationInfo.totalPages}
            >
              Próxima
            </button>
          </div>
        </nav>
      )}
    </div>
  );
}

export default ProjetoCompleto;
